'use client';
import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import type { ColDef } from 'ag-grid-community';

import styled from '@benzinga/themetron';
import Hooks from '@benzinga/hooks';
import { AUTH_SUCCESS_EVENT, AuthContainerV2 } from '@benzinga/auth-ui';
import { useIsUserLoggedIn, useIsUserPaywalled } from '@benzinga/user-context';
import Loader from './Loader';
import DateFilterBar from './DateFilterBar';
import type { GatedBlurResolved } from '../interfaces';
import { IDatePickerDateProps, ILayoutProps } from '../interfaces';
import Tabs from './Tabs';
import ProRecommendations from './ProRecommendations';
import { commaSeparatedSymbolsDisplay } from '@benzinga/utils';
import { useFilters } from '@benzinga/filter-ui';
import { ServerSideCalendar } from './ServerSideCalendar';
import { filterStateToQueryParams } from '../utils';

const OptimizelyBanner = dynamic(() => import('@benzinga/ads').then(module => module.OptimizelyBanner), {
  ssr: true,
});

const StockTable = React.lazy(() => import('./StockTable').then(module => ({ default: module.StockTable })));

const Layout: React.FC<ILayoutProps> = ({
  additionalGridOptions,
  blurColumnsWhenGated,
  calendar,
  calendarData,
  calendarType,
  columnDefs,
  defaultAdditionalParams = null,
  disableSettingGridApi,
  enableDatePicker = true,
  enableSearch,
  filterBarChildren,
  hasStockTableStyling,
  height,
  hiddenColumns,
  hiddenColumnsWhenGated,
  hideFilters,
  initialDateRange,
  initialTabIndex,
  isGated = false,
  isRouteLoading = false,
  leftSlot,
  noRowsOverlayText,
  onFilterChanged,
  onGridFilterChanged,
  onRowDataChanged,
  onTabChange,
  onTableReady = () => undefined,
  resolveGatedBlur,
  rowHeight,
  searchKey = 'tickers',
  searchMode = 'search',
  showProRecommendations = false,
  tableData,
  tableIndex = 0,
  tableLoading,
  tabs,
  theme = 'default',
  ticker,
  viewportChange,
}) => {
  const [filterParams, setFilterParams] = useFilters<Record<string, any>>(
    {
      date_from: initialDateRange ? initialDateRange.date_from : null,
      date_to: initialDateRange ? initialDateRange.date_to : null,
      ...(defaultAdditionalParams || {}),
      [`${searchKey}`]: ticker ?? null,
    },
    {
      hideQueryKeys: [...(enableDatePicker ? [] : ['date_from', 'date_to'])],
    },
  );

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>(tableData || []);
  const [search, setSearch] = useState(() => commaSeparatedSymbolsDisplay(filterParams[searchKey]));
  const mounted = Hooks.useIsMounted();

  const paywall = useIsUserPaywalled(
    isGated ? 'com/read' : '',
    isGated ? 'unlimited-calendars' : '',
    isGated ? 'hard' : 'soft-hard',
  );
  const [isPaywallActive, setIsPaywallActive] = React.useState(paywall?.active);

  // Dismiss the regwall instantly on successful register. useIsUserPaywalled
  // re-evaluates eventually, but listening here closes in the same tick.
  React.useEffect(() => {
    const handler = () => setIsPaywallActive(false);
    window.addEventListener(AUTH_SUCCESS_EVENT, handler);
    return () => window.removeEventListener(AUTH_SUCCESS_EVENT, handler);
  }, []);

  useEffect(() => {
    setIsPaywallActive(paywall?.active);
  }, [paywall.active]);

  const isLoggedIn = useIsUserLoggedIn();

  const gatedBlurConfig = React.useMemo((): GatedBlurResolved => {
    const defaultPass = (): GatedBlurResolved => ({
      blurColumnsWhenGatedResolved: blurColumnsWhenGated,
      columnDefsResolved: columnDefs,
      fullTableBlur: false,
      useColumnDefsForServerSide: false,
    });

    const resolved = resolveGatedBlur?.({
      blurColumnsWhenGated,
      calendar,
      columnDefs,
      isGated,
      isLoggedIn,
      isPaywallActive,
    });
    if (resolved) return { ...defaultPass(), ...resolved };
    return defaultPass();
  }, [blurColumnsWhenGated, calendar, columnDefs, isGated, isLoggedIn, isPaywallActive, resolveGatedBlur]);

  // mounted, onFilterChanged are excluded here, because of a infinite loading issue
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (mounted && onFilterChanged) {
      onFilterChanged(filterStateToQueryParams(filterParams));
    }
  }, [filterParams]);

  useEffect(() => {
    if (mounted && onFilterChanged) {
      const params = {
        ...filterParams,
        [`${searchKey}`]: ticker,
      };

      onFilterChanged(filterStateToQueryParams(params));
    }
  }, [ticker]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    setData(tableData ?? []);
  }, [tableData]);

  useEffect(() => {
    setLoading(tableLoading);
  }, [tableLoading]);

  function getFilterParameter(key: string) {
    return filterParams[key];
  }

  function onDateChange(date: IDatePickerDateProps) {
    if (date.interval) {
      const params = {
        ...filterParams,
        date_from: date.start,
        date_to: date.end,
      };

      setFilterParams(params);
    } else {
      setFilterParameter('date', date.start);
    }
  }

  function isDateChanged() {
    return (
      getFilterParameter('date_from') !== initialDateRange.date_from ||
      getFilterParameter('date_to') !== initialDateRange.date_to
    );
  }

  function setFilterParameter(key: string, value: string | number) {
    const params = {
      ...filterParams,
    };

    params[`${key}`] = value;
    setFilterParams(params);
  }

  function getParams(
    tickers: string | string[],
    dateFrom: string | null | undefined,
    dateTo: string | null | undefined,
  ): Record<string, string | string[] | null | undefined> {
    return {
      ...filterParams,
      date_from: dateFrom,
      date_to: dateTo,
      [`${searchKey}`]: tickers,
    };
  }

  function handleSearch(value: string | string[]) {
    const searchQuery = Array.isArray(value) ? value.join(',') : value;
    if (searchMode === 'tags') {
      if (!getFilterParameter(searchKey)) {
        setFilterParams(getParams(searchQuery, null, null));
      } else {
        setFilterParameter(searchKey, searchQuery);
      }

      // if (searchQuery && window && window.ga) {
      //   window.ga('send', 'event', 'User Interaction', 'Options Ticker Search', searchQuery);
      // }
    } else {
      const dateFrom = initialDateRange ? initialDateRange.date_from : null;
      const dateTo = initialDateRange ? initialDateRange.date_to : null;
      const tickers = ticker || '';

      if (!getFilterParameter(searchKey)) {
        const val = searchQuery.trim().toUpperCase();
        if (val === '') {
          setFilterParams(getParams(tickers, dateFrom, dateTo));
        } else {
          setFilterParams(getParams(val, null, null));
        }
      } else {
        const val = searchQuery.trim().toUpperCase();
        if (val === '') {
          setFilterParams(getParams(tickers, dateFrom, dateTo));
        } else {
          setFilterParameter(searchKey, val);
        }
      }

      setSearch(searchQuery);
      // if (value && window && window.ga) {
      //   window.ga('send', 'event', 'User Interaction', 'Options Ticker Search', value);
      // }
    }
  }

  const isServerSideCalendar = !!(calendarType === 'server_side' || calendarData?.serverSide);
  const showServerTable = isServerSideCalendar && !mounted;
  const tableDataForDisplay = (data?.length ? data : tableData) ?? [];

  const ServerTableContent: React.FC<{ sliceData?: boolean }> = ({ sliceData }) => {
    if (!calendarData) return null;
    return (
      <div className="server-side-calendar-container" style={{ minHeight: height ?? 600 }}>
        <ServerSideCalendar
          agGridColumnDefs={
            gatedBlurConfig.useColumnDefsForServerSide ? (gatedBlurConfig.columnDefsResolved as ColDef[]) : undefined
          }
          blurColumnsWhenGated={gatedBlurConfig.blurColumnsWhenGatedResolved}
          calendar={calendar}
          calendarData={calendarData}
          calendarDataSet={sliceData ? tableDataForDisplay.slice(0, 30) : tableDataForDisplay}
          fullTableBlur={gatedBlurConfig.fullTableBlur}
          hasStockTableStyling={hasStockTableStyling}
          height={height}
          hiddenColumns={hiddenColumns}
          hiddenColumnsWhenGated={hiddenColumnsWhenGated}
          isGated={isPaywallActive}
          noDataText={noRowsOverlayText}
          showAd={false}
          ticker={ticker}
        />
      </div>
    );
  };

  const EdgeAd = () => (
    <OptimizelyBanner
      adType="calendar"
      buttonText="Sign up for Benzinga Edge"
      subtitle="Unlock all calendars and get access to the best trading ideas, research, and analysis."
      title="Access with Benzinga Edge"
      utmSource={`calendar-${calendar}`}
    />
  );

  return (
    <CalendarLayoutWrapper
      className="calendar-table relative"
      style={isServerSideCalendar ? { minHeight: (height ?? 600) + 120 } : undefined}
    >
      {!hideFilters && (
        <div className="ratings-filters">
          {tabs && onTabChange ? (
            <>
              <Loader loading={isRouteLoading} />
              <Tabs disabled={isRouteLoading} initialTabIndex={initialTabIndex} onChange={onTabChange} tabs={tabs} />
            </>
          ) : (
            leftSlot
          )}

          {filterBarChildren}

          <DateFilterBar
            dateRange={{
              date_from: getFilterParameter('date_from'),
              date_to: getFilterParameter('date_to'),
            }}
            enableDatePicker={enableDatePicker}
            hideSearch={!enableSearch || !!ticker}
            onDateChange={onDateChange}
            onSearch={handleSearch}
            search={search}
            tagsMode={searchMode === 'tags'}
            theme={theme}
          />
        </div>
      )}
      {showServerTable ? (
        <ServerTableContent />
      ) : (
        <>
          <Loader loading={loading} />
          <React.Suspense fallback={<ServerTableContent sliceData />}>
            <StockTable
              additionalGridOptions={additionalGridOptions}
              blurColumnsWhenGated={gatedBlurConfig.blurColumnsWhenGatedResolved}
              calendarName={calendar}
              columnDefs={gatedBlurConfig.columnDefsResolved}
              data={data}
              disableSettingGridApi={disableSettingGridApi}
              enableSearch={!!enableSearch}
              externalFilterTriggered={isDateChanged()}
              fullTableBlur={gatedBlurConfig.fullTableBlur}
              height="60vh"
              hiddenColumns={hiddenColumns}
              hiddenColumnsWhenGated={hiddenColumnsWhenGated}
              isGated={isPaywallActive}
              noRowsOverlayText={isPaywallActive ? '__HIDE__' : noRowsOverlayText}
              onFilterChanged={onGridFilterChanged}
              onReady={onTableReady}
              onRowDataChanged={onRowDataChanged}
              onSearch={handleSearch}
              restrictTableHeader={gatedBlurConfig.restrictTableHeader}
              rowHeight={rowHeight}
              search={search}
              setSearch={setSearch}
              showAd={false}
              theme="dark-blue"
              ticker={ticker}
              viewportChange={viewportChange}
            />
          </React.Suspense>
        </>
      )}
      {showProRecommendations && <ProRecommendations rowData={data} />}
      {isPaywallActive && isLoggedIn && (
        <div className="absolute top-1/3 w-full flex justify-center items-center">
          <EdgeAd />
        </div>
      )}
      {tableIndex === 0 && isPaywallActive && isGated && !isLoggedIn && (
        <AuthContainerV2 placement={'calendar - ' + calendarData?.title} surface="regwall" />
      )}
    </CalendarLayoutWrapper>
  );
};

export default Layout;

const CalendarLayoutWrapper = styled.div`
  position: relative;

  .status-panel-container {
    margin-bottom: 4px;
    top: 4px;
    .clear-filters {
      text-decoration: underline;
      color: #2ca2d1;
      cursor: pointer;
    }
  }

  .bz-ag-table {
    margin-top: 0;
    margin-bottom: 10px;

    .ag-react-container {
      width: 100%;
    }

    a {
      color: #2ca2d1;
    }

    &__company-image {
      max-height: 18px;
      margin-left: 0.25rem;
      float: right;
    }

    &__analyst-name {
      overflow: hidden;
      white-space: nowrap;
      width: 100%;
    }

    &__analyst-smart-score {
      color: white;
      background: ${({ theme }) => theme.colorPalette.blue600};
      padding: 2px 4px;
      width: 35px;
      max-width: 35px;
      line-height: 20px;
      height: 20px;
      max-height: 20px;
      text-align: center;
      border-radius: ${({ theme }) => theme.borderRadius.full};
      margin-left: 0.1rem;
      float: right;
      display: flex;
      align-items: center;
      justify-content: center;

      &.green {
        background: ${({ theme }) => theme.colorPalette.green500};
      }

      &.yellow {
        background: ${({ theme }) => theme.colorPalette.yellow400};
      }

      &.orange {
        background: ${({ theme }) => theme.colorPalette.orange400};
      }

      &.red {
        background: ${({ theme }) => theme.colorPalette.red600};
      }
    }

    .country-cell {
      & > span {
        display: flex;
        align-items: center;
      }
    }
  }

  div.company-name-cell {
    width: 100%;
    display: inline-flex;
    justify-content: space-between;
    line-height: 19px;

    span {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  .server-side-calendar-container {
    display: flex;
    width: 100%;
    overflow-x: auto;
  }

  .calendar-table-link,
  .calendar-table-ticker-link {
    color: #2ba2d1;
    text-decoration: none;

    &:hover {
      color: #2ba2d1;
      text-decoration: underline;
    }
  }

  .calendar-table-ticker-link {
    font-weight: 600;

    &:hover {
      text-decoration: none;
    }
  }

  .analyst-name-and-smart-score-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .ratings-filters {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
    width: 100%;
    margin-bottom: 1rem;
    position: relative;
    margin-bottom: 1rem;
    gap: 0.5rem;

    .dates-filter {
      .range-date-picker {
        &__picker-field {
          font-weight: 500;
        }
      }
      @media screen and (max-width: 500px) {
        flex-wrap: nowrap;

        .range-date-picker {
          padding-left: 0;

          &__field-wrapper {
            width: 153px;
          }

          &__picker-field {
            font-size: 10px;
          }
        }

        .bz3-search-bar-wrapper {
          width: auto;
          margin-left: 5px;
        }
      }
    }
  }
`;
